import { useEffect, useState, ReactNode } from "react";
import { getMembership, login, logout, Membership, USER_GROUP } from "../lib/auth";

type GuardState =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "no-access"; membership: Membership }
  | { status: "signed-in"; membership: Membership };

export function AuthGuard({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GuardState>({ status: "loading" });

  useEffect(() => {
    (async () => {
      const membership = await getMembership();
      if (!membership) {
        setState({ status: "signed-out" });
      } else if (!membership.isMember) {
        setState({ status: "no-access", membership });
      } else {
        setState({ status: "signed-in", membership });
      }
    })();
  }, []);

  if (state.status === "loading") {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }

  if (state.status === "signed-out") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-semibold text-slate-800">Cockpit</h1>
        <p className="text-slate-600">Sign in with your Cloudar account.</p>
        <button
          onClick={() => login()}
          className="rounded-md bg-slate-800 px-4 py-2 text-white hover:bg-slate-700"
        >
          Sign in
        </button>
      </div>
    );
  }

  if (state.status === "no-access") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <h1 className="text-2xl font-semibold text-slate-800">No access</h1>
        <p className="max-w-md text-slate-600">
          You're signed in as{" "}
          <span className="font-medium">{state.membership.email ?? "—"}</span>,
          but you're not in the <code>{USER_GROUP}</code> group.
        </p>
        <p className="max-w-md text-sm text-slate-500">
          Ask a Cockpit admin to add you to the group, then sign in again.
        </p>
        <button
          onClick={() => logout()}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
        >
          Sign out
        </button>
      </div>
    );
  }

  return <>{children}</>;
}
